import {
  businessClientDetailSchema,
  businessClientsSchema,
  businessQuoteSchema,
  type BusinessClientDetail,
  type BusinessClients,
  type BusinessQuote,
  type CreateQuote,
  type QuoteStatus,
  type UpdateClient,
  businessCardSchema,
  businessProfileSchema,
  businessStateSchema,
  quickReplySchema,
  type BusinessCard,
  type BusinessKind,
  type BusinessProfile,
  type BusinessState,
  type QuickReply,
  type SaveBusinessProfile,
} from '@secretario/shared';
import { apiFetch } from './http.js';

export const BUSINESS_KEY = ['business'] as const;

export async function getBusiness(): Promise<BusinessState> {
  return businessStateSchema.parse(await apiFetch<unknown>('/v1/business'));
}

export async function saveBusiness(patch: SaveBusinessProfile): Promise<BusinessProfile> {
  return businessProfileSchema.parse(
    await apiFetch<unknown>('/v1/business', { method: 'PUT', body: patch }),
  );
}

/** Proposta de roteiro para um tipo de negócio sem roteiro pronto. */
export function draftBusinessGuidance(): Promise<{ guidance: string }> {
  return apiFetch<{ guidance: string }>('/v1/business/guidance-draft', { method: 'POST' });
}

export async function addQuickReply(input: Omit<QuickReply, 'id'>): Promise<QuickReply> {
  return quickReplySchema.parse(
    await apiFetch<unknown>('/v1/business/replies', { method: 'POST', body: input }),
  );
}

export async function updateQuickReply(
  id: string,
  input: Omit<QuickReply, 'id'>,
): Promise<QuickReply> {
  return quickReplySchema.parse(
    await apiFetch<unknown>(`/v1/business/replies/${id}`, { method: 'PUT', body: input }),
  );
}

export function removeQuickReply(id: string): Promise<void> {
  return apiFetch<void>(`/v1/business/replies/${id}`, { method: 'DELETE' });
}

/** Cartão público de um negócio (não precisa de conta). */
export async function getBusinessCard(username: string): Promise<BusinessCard> {
  return businessCardSchema.parse(
    await apiFetch<unknown>(`/v1/public/business/${encodeURIComponent(username)}`, { auth: false }),
  );
}

/** Avisa o negócio de que cheguei pelo link de atendimento (viro contato dele). */
export function visitBusiness(username: string): Promise<void> {
  return apiFetch<void>(`/v1/business/${encodeURIComponent(username)}/visit`, { method: 'POST' });
}

/** Cena 3D de cada tipo de negócio (o ambiente, sem personagem). */
export function businessScene(kind: BusinessKind): string {
  return `/business/${kind}.webp`;
}

// ── Controle do negócio ────────────────────────────────────────────────────
export const CLIENTS_KEY = ['business-clients'] as const;

export async function getClients(): Promise<BusinessClients> {
  return businessClientsSchema.parse(await apiFetch<unknown>('/v1/business/clients'));
}

export async function getClient(contactId: string): Promise<BusinessClientDetail> {
  return businessClientDetailSchema.parse(
    await apiFetch<unknown>(`/v1/business/clients/${encodeURIComponent(contactId)}`),
  );
}

export async function updateClient(
  contactId: string,
  patch: UpdateClient,
): Promise<BusinessClientDetail> {
  return businessClientDetailSchema.parse(
    await apiFetch<unknown>(`/v1/business/clients/${encodeURIComponent(contactId)}`, {
      method: 'PUT',
      body: patch,
    }),
  );
}

export async function createQuote(input: CreateQuote): Promise<BusinessQuote> {
  return businessQuoteSchema.parse(
    await apiFetch<unknown>('/v1/business/quotes', { method: 'POST', body: input }),
  );
}

export async function setQuoteStatus(id: string, status: QuoteStatus): Promise<BusinessQuote> {
  return businessQuoteSchema.parse(
    await apiFetch<unknown>(`/v1/business/quotes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: { status },
    }),
  );
}

export function followUpDraft(contactId: string): Promise<{ text: string }> {
  return apiFetch<{ text: string }>(
    `/v1/business/follow-ups/${encodeURIComponent(contactId)}/draft`,
    { method: 'POST' },
  );
}

export function followUpSent(contactId: string): Promise<void> {
  return apiFetch<void>(`/v1/business/follow-ups/${encodeURIComponent(contactId)}/sent`, {
    method: 'POST',
  });
}

/**
 * Manda um texto na conversa com o cliente. `asSecretary`: sai como fala do
 * Secretário do dono (retomada); senão, como mensagem do próprio dono (orçamento).
 */
export async function sendToClient(
  username: string,
  text: string,
  asSecretary?: { name: string; characterKey: string; ownerName: string },
): Promise<string> {
  const [matrix, rooms] = await Promise.all([
    import('../matrix/client.js'),
    import('../matrix/rooms.js'),
  ]);
  const client = await matrix.ensureMatrixClient();
  if (!client) throw new Error('Não consegui abrir o mensageiro agora. Tenta de novo?');
  const roomId = await rooms.startDirectMessage(client, `@${username}:${client.getDomain()}`);
  if (asSecretary) {
    await rooms.postAssistantMessage(client, roomId, { ...asSecretary, text, request: '' });
  } else {
    await rooms.sendText(client, roomId, text);
  }
  return roomId;
}

/** Abre (ou cria) a conversa com o cliente, sem mandar nada. */
export async function openClientConversation(username: string): Promise<string> {
  const [matrix, rooms] = await Promise.all([
    import('../matrix/client.js'),
    import('../matrix/rooms.js'),
  ]);
  const client = await matrix.ensureMatrixClient();
  if (!client) throw new Error('Não consegui abrir o mensageiro agora. Tenta de novo?');
  return rooms.startDirectMessage(client, `@${username}:${client.getDomain()}`);
}
